import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import {
  createPettyCashMovement,
  getPettyCashSummary,
  listPettyCashMovements,
  type PettyCashMovementItem,
  type PettyCashSummary,
} from "@/lib/api";
import NuevoMovimientoModal from "./components/NuevoMovimientoModal";
import type { CategoriaMovimiento, MovimientoCaja, TipoMovimiento } from "./types";

type FiltroTipo = "todos" | TipoMovimiento;

const categoriaIcon: Record<string, string> = {
  "Recaudación efectivo": "ri-money-dollar-circle-line",
  "Aporte SUBDERE": "ri-government-line",
  "Otros ingresos": "ri-add-circle-line",
  "Materiales y herramientas": "ri-tools-line",
  "Pago proveedor": "ri-truck-line",
  "Servicios básicos": "ri-flashlight-line",
  Combustible: "ri-gas-station-line",
  "Gastos administrativos": "ri-file-list-3-line",
  Mantenimiento: "ri-settings-3-line",
  "Otros egresos": "ri-subtract-line",
};

const allCategories: CategoriaMovimiento[] = [
  "Recaudación efectivo",
  "Aporte SUBDERE",
  "Otros ingresos",
  "Materiales y herramientas",
  "Pago proveedor",
  "Servicios básicos",
  "Combustible",
  "Gastos administrativos",
  "Mantenimiento",
  "Otros egresos",
];

export default function CajaChicaPage() {
  const navigate = useNavigate();
  const { session } = useSession();

  const [movimientos, setMovimientos] = useState<MovimientoCaja[]>([]);
  const [summary, setSummary] = useState<PettyCashSummary>({
    saldoInicial: 0,
    totalIngresos: 0,
    totalEgresos: 0,
    saldoActual: 0,
    movimientosIngresos: 0,
    movimientosEgresos: 0,
  });

  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>("todos");
  const [filtroCategoria, setFiltroCategoria] = useState<CategoriaMovimiento | "todas">("todas");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadData(): Promise<void> {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      const [rows, nextSummary] = await Promise.all([
        listPettyCashMovements(session, { limit: 500 }),
        getPettyCashSummary(session),
      ]);
      setMovimientos(rows.map(mapMovementToView));
      setSummary(nextSummary);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cargar caja chica");
      setMovimientos([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.accessToken]);

  const stats = useMemo(
    () => ({
      totalIngresos: summary.totalIngresos,
      totalEgresos: summary.totalEgresos,
      saldoActual: summary.saldoActual,
      saldoInicial: summary.saldoInicial,
      ingresosCount: summary.movimientosIngresos,
      egresosCount: summary.movimientosEgresos,
    }),
    [summary],
  );

  const categorias = useMemo(() => {
    const set = new Set(
      movimientos
        .map((m) => m.categoria)
        .filter((category): category is CategoriaMovimiento => allCategories.includes(category as CategoriaMovimiento)),
    );
    return Array.from(set) as CategoriaMovimiento[];
  }, [movimientos]);

  const filtered = useMemo(() => {
    return movimientos
      .filter((m) => {
        const matchTipo = filtroTipo === "todos" || m.tipo === filtroTipo;
        const matchCat = filtroCategoria === "todas" || m.categoria === filtroCategoria;
        const matchSearch =
          search === "" ||
          m.descripcion.toLowerCase().includes(search.toLowerCase()) ||
          m.responsable.toLowerCase().includes(search.toLowerCase()) ||
          (m.comprobante ?? "").toLowerCase().includes(search.toLowerCase());
        return matchTipo && matchCat && matchSearch;
      })
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }, [movimientos, filtroTipo, filtroCategoria, search]);

  async function handleNuevoMovimiento(mov: MovimientoCaja): Promise<void> {
    if (!session) return;
    await createPettyCashMovement(session, {
      movementDate: mov.fecha,
      movementType: mov.tipo,
      category: mov.categoria,
      description: mov.descripcion,
      amount: mov.monto,
      responsible: mov.responsable,
      voucherNumber: mov.comprobante ?? undefined,
      notes: mov.observacion ?? undefined,
    });
    setShowModal(false);
    await loadData();
  }

  const detalleMovimiento = detalleId ? movimientos.find((m) => m.id === detalleId) : null;

  const tipoFiltros: { key: FiltroTipo; label: string }[] = [
    { key: "todos", label: "Todos" },
    { key: "ingreso", label: "Ingresos" },
    { key: "egreso", label: "Egresos" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/administracion")}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Caja Chica</h1>
            <p className="text-sm text-gray-500 mt-0.5">Control de ingresos y egresos</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Nuevo movimiento
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50">
              <i className="ri-safe-line text-teal-600" />
            </div>
            <p className="text-xs text-gray-400 font-medium">Saldo actual</p>
          </div>
          <p className={`text-2xl font-bold ${stats.saldoActual >= 0 ? "text-teal-700" : "text-red-600"}`}>
            ${stats.saldoActual.toLocaleString("es-CL")}
          </p>
          <p className="text-xs text-gray-400 mt-1">Saldo inicial: ${stats.saldoInicial.toLocaleString("es-CL")}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50">
              <i className="ri-arrow-down-circle-line text-emerald-600" />
            </div>
            <p className="text-xs text-gray-400 font-medium">Total ingresos</p>
          </div>
          <p className="text-2xl font-bold text-emerald-700">+${stats.totalIngresos.toLocaleString("es-CL")}</p>
          <p className="text-xs text-gray-400 mt-1">{stats.ingresosCount} movimientos</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50">
              <i className="ri-arrow-up-circle-line text-red-500" />
            </div>
            <p className="text-xs text-gray-400 font-medium">Total egresos</p>
          </div>
          <p className="text-2xl font-bold text-red-600">-${stats.totalEgresos.toLocaleString("es-CL")}</p>
          <p className="text-xs text-gray-400 mt-1">{stats.egresosCount} movimientos</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Buscar por descripción, responsable o comprobante..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
            />
          </div>
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value as CategoriaMovimiento | "todas")}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors cursor-pointer bg-white"
          >
            <option value="todas">Todas las categorías</option>
            {categorias.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 mt-3">
          {tipoFiltros.map((f) => (
            <button
              key={f.key}
              onClick={() => setFiltroTipo(f.key)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                filtroTipo === f.key
                  ? "bg-teal-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f.label}
              {f.key !== "todos" && (
                <span className="ml-1 opacity-70">
                  ({movimientos.filter((m) => m.tipo === f.key).length})
                </span>
              )}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400">{filtered.length} movimiento{filtered.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Fecha</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Descripción</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Categoría</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Responsable</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Comprobante</th>
              <th className="text-right text-xs font-semibold text-gray-500 px-5 py-3">Monto</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center py-12 text-sm text-gray-400">
                  Cargando movimientos...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12 text-sm text-gray-400">
                  <i className="ri-inbox-line text-3xl block mb-2 text-gray-300" />
                  No se encontraron movimientos con los filtros aplicados
                </td>
              </tr>
            ) : (
              filtered.map((mov, i) => (
                <tr
                  key={mov.id}
                  className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i === filtered.length - 1 ? "border-b-0" : ""}`}
                >
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-600">{new Date(mov.fecha).toLocaleDateString("es-CL")}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${mov.tipo === "ingreso" ? "bg-emerald-100" : "bg-red-100"}`}>
                        <i className={`text-xs ${mov.tipo === "ingreso" ? "ri-arrow-down-line text-emerald-600" : "ri-arrow-up-line text-red-500"}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{mov.descripcion}</p>
                        {mov.observacion && <p className="text-xs text-gray-400 truncate max-w-[200px]">{mov.observacion}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <i className={`${categoriaIcon[mov.categoria] ?? "ri-price-tag-3-line"} text-gray-400 text-sm`} />
                      <span className="text-xs text-gray-600">{mov.categoria}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-600">{mov.responsable}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    {mov.comprobante ? (
                      <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{mov.comprobante}</span>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <span className={`text-sm font-semibold ${mov.tipo === "ingreso" ? "text-emerald-600" : "text-red-600"}`}>
                      {mov.tipo === "ingreso" ? "+" : "-"}${mov.monto.toLocaleString("es-CL")}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => setDetalleId(mov.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400 hover:text-gray-600"
                    >
                      <i className="ri-eye-line text-sm" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detalleMovimiento && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800">Detalle del movimiento</h2>
              <button
                onClick={() => setDetalleId(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className={`flex items-center gap-3 p-3 rounded-xl ${detalleMovimiento.tipo === "ingreso" ? "bg-emerald-50" : "bg-red-50"}`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${detalleMovimiento.tipo === "ingreso" ? "bg-emerald-100" : "bg-red-100"}`}>
                  <i className={`text-xl ${detalleMovimiento.tipo === "ingreso" ? "ri-arrow-down-circle-line text-emerald-600" : "ri-arrow-up-circle-line text-red-500"}`} />
                </div>
                <div>
                  <p className={`text-lg font-bold ${detalleMovimiento.tipo === "ingreso" ? "text-emerald-700" : "text-red-600"}`}>
                    {detalleMovimiento.tipo === "ingreso" ? "+" : "-"}${detalleMovimiento.monto.toLocaleString("es-CL")}
                  </p>
                  <p className={`text-xs font-medium capitalize ${detalleMovimiento.tipo === "ingreso" ? "text-emerald-600" : "text-red-500"}`}>
                    {detalleMovimiento.tipo}
                  </p>
                </div>
              </div>

              {[
                { label: "ID", value: detalleMovimiento.id },
                { label: "Fecha", value: new Date(detalleMovimiento.fecha).toLocaleDateString("es-CL", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) },
                { label: "Descripción", value: detalleMovimiento.descripcion },
                { label: "Categoría", value: detalleMovimiento.categoria },
                { label: "Responsable", value: detalleMovimiento.responsable },
                { label: "Comprobante", value: detalleMovimiento.comprobante ?? "Sin comprobante" },
                ...(detalleMovimiento.observacion ? [{ label: "Observación", value: detalleMovimiento.observacion }] : []),
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-start gap-4">
                  <span className="text-xs font-semibold text-gray-400 w-28 flex-shrink-0">{label}</span>
                  <span className="text-sm text-gray-700 text-right">{value}</span>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setDetalleId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <NuevoMovimientoModal
          onClose={() => setShowModal(false)}
          onConfirm={(movement) => {
            void handleNuevoMovimiento(movement);
          }}
        />
      )}
    </div>
  );
}

function mapMovementToView(item: PettyCashMovementItem): MovimientoCaja {
  const category = allCategories.includes(item.category as CategoriaMovimiento)
    ? (item.category as CategoriaMovimiento)
    : "Otros egresos";
  return {
    id: item.id,
    fecha: item.movementDate,
    tipo: item.movementType,
    categoria: category,
    descripcion: item.description,
    monto: item.amount,
    responsable: item.responsible,
    comprobante: item.voucherNumber,
    observacion: item.notes,
  };
}
